import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  company: z.string().optional(),
  email: z.string().email("Enter a valid email"),
  phone: z.string().optional(),
  topic: z.string().min(2, "Let us know what this is about"),
  message: z.string().min(10, "Tell us a bit more (min 10 characters)"),
});

const FIELD = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#07111F] outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-[#07111F]";

export default function ContactForm() {
  const [done, setDone] = useState(false);
  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { name: "", company: "", email: "", phone: "", topic: "", message: "" } });

  const onSubmit = async (values) => {
    try {
      await axios.post(`${API}/contact`, values);
      setDone(true);
      reset();
      toast.success("Message sent — we'll be in touch shortly.");
      setTimeout(() => setDone(false), 6000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    }
  };

  const Err = ({ name }) => errors[name] ? (
    <p className="mt-1.5 text-xs text-red-500" data-testid={`error-${name}`}>{errors[name].message}</p>
  ) : null;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="contact-form" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="overline text-slate-400" htmlFor="name">Name</label>
          <input id="name" className={`mt-2 ${FIELD}`} placeholder="Your full name" data-testid="input-name" {...register("name")} />
          <Err name="name" />
        </div>
        <div>
          <label className="overline text-slate-400" htmlFor="company">Company</label>
          <input id="company" className={`mt-2 ${FIELD}`} placeholder="Company (optional)" data-testid="input-company" {...register("company")} />
        </div>
        <div>
          <label className="overline text-slate-400" htmlFor="email">Email</label>
          <input id="email" type="email" className={`mt-2 ${FIELD}`} placeholder="you@company.com" data-testid="input-email" {...register("email")} />
          <Err name="email" />
        </div>
        <div>
          <label className="overline text-slate-400" htmlFor="phone">Phone</label>
          <input id="phone" className={`mt-2 ${FIELD}`} placeholder="Phone (optional)" data-testid="input-phone" {...register("phone")} />
        </div>
        <div className="sm:col-span-2">
          <label className="overline text-slate-400" htmlFor="topic">What can we help you with?</label>
          <input id="topic" className={`mt-2 ${FIELD}`} placeholder="e.g. AI automation, smart home, software build" data-testid="input-topic" {...register("topic")} />
          <Err name="topic" />
        </div>
        <div className="sm:col-span-2">
          <label className="overline text-slate-400" htmlFor="message">Message</label>
          <textarea id="message" rows={5} className={`mt-2 resize-none ${FIELD}`} placeholder="Tell us what you're trying to build, automate, or improve." data-testid="input-message" {...register("message")} />
          <Err name="message" />
        </div>
      </div>

      <button type="submit" disabled={isSubmitting} className="btn-primary mt-6 w-full disabled:opacity-70" data-testid="submit-contact">
        {isSubmitting ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>)
          : done ? (<><CheckCircle2 size={16} /> Sent</>)
          : (<>Start a Conversation <ArrowUpRight size={16} /></>)}
      </button>

      {done && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 text-center text-sm text-emerald-600" data-testid="success-message">
          Thanks for reaching out. We'll get back to you shortly.
        </motion.p>
      )}
    </form>
  );
}
